import { Box } from '@material-ui/core'
import { ArrowBackIos, ArrowForwardIos, Link } from '@material-ui/icons'
import { Button } from 'components/facets'
import powertoolsLogo from 'img/blue-logo.svg'
import cardmarketLogoFull from 'img/cardmarketLogoFull.svg'
import { useInventoryStore } from 'lib/hooks/useInventoryStore'
import { Fragment, useEffect, useState } from 'react'
import { AggregatedInventoryArticle, INVENTORY_TRANSACTION_SOURCES } from 'shared'
import { DiffCell } from './DiffCell'

export const DiffViewer = (): JSX.Element => {
  const {
    numSolvedConflicts,
    setNumSolvedConflicts,
    diffSelectedArticles,
    setDiffSelectedArticles,
    paddedLeftData,
    setPaddedLeftData,
    paddedRightData,
    setPaddedRightData,
  } = useInventoryStore()

  const [currentPage, setCurrentPage] = useState(1)

  const itemsPerPage = 50
  const [totalPages, setTotalPages] = useState(0)
  const [currentLeftItems, setCurrentLeftItems] = useState<
    (AggregatedInventoryArticle | undefined)[]
  >([])
  const [currentRightItems, setCurrentRightItems] = useState<
    (AggregatedInventoryArticle | undefined)[]
  >([])

  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const handleNextPage = () => {
    // setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
    setCurrentPage(currentPage + 1)
  }

  useEffect(() => {
    if (paddedLeftData.length > 0 || paddedRightData.length > 0) {
      setTotalPages(Math.ceil(paddedLeftData.length / itemsPerPage))
      setCurrentLeftItems(paddedLeftData.slice(startIndex, endIndex))
      setCurrentRightItems(paddedRightData.slice(startIndex, endIndex))
    }
  }, [startIndex, endIndex])

  const getTable = (data: (AggregatedInventoryArticle | undefined)[], side: string) => {
    const iconHeight = '16px'

    const isCellDisabled = (row: AggregatedInventoryArticle, index: number, side: string) => {
      let conflictingArticle: AggregatedInventoryArticle | undefined
      if (side === 'left') {
        conflictingArticle = paddedRightData[index]
      } else if (side === 'right') {
        conflictingArticle = paddedLeftData[index]
      }

      if (!conflictingArticle) {
        return !row.isSelectedForConflictResolution
      }
      if (row.hasConflict) {
        return (
          !row.isSelectedForConflictResolution && conflictingArticle.isSelectedForConflictResolution
        )
      }
      return false
    }

    const handleCheckChange = (row: AggregatedInventoryArticle, index: number, side: string) => {
      const alreadyChecked = row.isSelectedForConflictResolution
      let newNumSolvedConflicts = numSolvedConflicts
      let decrementSolvedConflicts = false
      let incrementSolvedConflicts = false
      const newPaddedLeftData = [...paddedLeftData]
      const newPaddedRightData = [...paddedRightData]
      const newDiffSelectedArticles = [...diffSelectedArticles]

      let conflictingArticle: AggregatedInventoryArticle | undefined
      if (side === 'left') {
        conflictingArticle = currentRightItems[index]
      } else if (side === 'right') {
        conflictingArticle = currentLeftItems[index]
      }

      if (alreadyChecked && conflictingArticle) {
        return
      }

      if (!conflictingArticle) {
        // we are here if the user has clicked on a row that was already checked
        // and that has no conflict, so it's possible to uncheck it
        row.isSelectedForConflictResolution = !alreadyChecked
        if (alreadyChecked) {
          const rowArticleIndex = newDiffSelectedArticles.findIndex(
            (article) =>
              article.idProduct === row.idProduct &&
              article.card.expansion === row.card.expansion &&
              article.idLanguage === row.idLanguage &&
              article.condition === row.condition &&
              article.quantity === row.quantity &&
              article.finishType === row.finishType &&
              article.isFirstEd === row.isFirstEd &&
              article.isSigned === row.isSigned &&
              article.countryEdition === row.countryEdition &&
              article.gradingCompany === row.gradingCompany &&
              article.grade === row.grade
          )
          if (rowArticleIndex !== -1) {
            newDiffSelectedArticles.splice(rowArticleIndex, 1)
          }
        } else {
          newDiffSelectedArticles.push(row)
        }

        if (side === 'left') {
          row.source = INVENTORY_TRANSACTION_SOURCES.POWERTOOLS
          newPaddedLeftData[index] = row
        } else if (side === 'right') {
          row.source = INVENTORY_TRANSACTION_SOURCES.CARDMARKET
          newPaddedRightData[index] = row
        }
        setPaddedLeftData(newPaddedLeftData)
        setPaddedRightData(newPaddedRightData)
        setDiffSelectedArticles(newDiffSelectedArticles)

        return
      }

      if (conflictingArticle.isSelectedForConflictResolution) {
        conflictingArticle.isSelectedForConflictResolution = false
        const conflictingArticleIndex = newDiffSelectedArticles.findIndex(
          (article) => article.idProduct === conflictingArticle?.idProduct
        )
        if (conflictingArticleIndex !== -1) {
          newDiffSelectedArticles.splice(conflictingArticleIndex, 1)
        }

        if (row.hasConflict) {
          decrementSolvedConflicts = true
        }
      }
      if (alreadyChecked) {
        row.isSelectedForConflictResolution = false
        const rowArticleIndex = newDiffSelectedArticles.findIndex(
          (article) => article.idProduct === row.idProduct
        )
        if (rowArticleIndex !== -1) {
          newDiffSelectedArticles.splice(rowArticleIndex, 1)
        }

        if (row.hasConflict) {
          decrementSolvedConflicts = true
        }
      } else {
        row.isSelectedForConflictResolution = true
        newDiffSelectedArticles.push(row)

        if (row.hasConflict) {
          incrementSolvedConflicts = true
        }
      }

      if (side === 'left') {
        row.source = INVENTORY_TRANSACTION_SOURCES.POWERTOOLS
        newPaddedLeftData[index] = row
        newPaddedRightData[index] = conflictingArticle
      } else if (side === 'right') {
        row.source = INVENTORY_TRANSACTION_SOURCES.CARDMARKET
        newPaddedRightData[index] = row
        newPaddedLeftData[index] = conflictingArticle
      }
      setPaddedLeftData(newPaddedLeftData)
      setPaddedRightData(newPaddedRightData)
      setDiffSelectedArticles(newDiffSelectedArticles)

      // update num solved conflicts
      if (decrementSolvedConflicts) {
        newNumSolvedConflicts--
      }
      if (incrementSolvedConflicts) {
        newNumSolvedConflicts++
      }
      setNumSolvedConflicts(newNumSolvedConflicts)
    }

    const getEmptyDiffCell = (side: string, index: number) => {
      return (
        <Box
          data-testid={`${side}_${index}_empty`}
          key={`${side}_${index + itemsPerPage * (currentPage - 1)}`}
          className="diffEmptyCell"
          style={{
            backgroundColor: `${index % 2 === 0 ? '#d9e6f5' : '#0d71c345'}`,
          }}
        />
      )
    }

    return (
      <>
        {data.map((row, index) => (
          <Fragment key={`${side}_${index + itemsPerPage * (currentPage - 1)}`}>
            {row ? (
              <>
                <DiffCell
                  side={side}
                  index={index}
                  row={row}
                  iconHeight={iconHeight}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  isCellDisabled={() =>
                    isCellDisabled(row, index + itemsPerPage * (currentPage - 1), side)
                  }
                  handleCheckChange={handleCheckChange}
                />
              </>
            ) : (
              <>{getEmptyDiffCell(side, index)}</>
            )}
          </Fragment>
        ))}
      </>
    )
  }

  const getLinkIcons = (data: (AggregatedInventoryArticle | undefined)[]) => {
    return (
      <>
        {data.map((row, index) => {
          return (
            <Fragment key={index + itemsPerPage * (currentPage - 1)}>
              {row && row.hasConflict && (
                <Box display="flex" height="105px" justifyContent="center" alignItems="center">
                  <Link />
                </Box>
              )}
            </Fragment>
          )
        })}
      </>
    )
  }

  const PaginationControls = (): JSX.Element => {
    return (
      <Box display="flex" mt={2} justifyContent="center">
        <Button onClick={handlePrevPage} disabled={currentPage === 1}>
          <ArrowBackIos />
        </Button>
        <Box alignSelf="center">
          {currentPage} / {totalPages}
        </Box>
        <Button onClick={handleNextPage} disabled={currentPage === totalPages}>
          <ArrowForwardIos />
        </Button>
      </Box>
    )
  }

  const diffCellWidth = 400
  const linkCellWidth = 35
  const logoImgHeight = 40

  return (
    <>
      <Box width={diffCellWidth * 2 + linkCellWidth}>
        <PaginationControls />

        <Box display="flex" justifyContent="center">
          <Box width={diffCellWidth}>
            <Box textAlign="center" marginBottom={2}>
              <img src={powertoolsLogo} height={logoImgHeight} />
            </Box>
            {getTable(currentLeftItems, 'left')}
          </Box>
          <Box width={linkCellWidth}>
            <Box textAlign="center" marginBottom={2}>
              <Box height="40px">&nbsp;</Box>
            </Box>
            {getLinkIcons(currentLeftItems)}
          </Box>
          <Box width={diffCellWidth}>
            <Box textAlign="center" marginBottom={2}>
              <img src={cardmarketLogoFull} height={logoImgHeight} />
            </Box>
            {getTable(currentRightItems, 'right')}
          </Box>
        </Box>

        <PaginationControls />
      </Box>
    </>
  )
}
